import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';

export interface GemstoneLine {
  type: string;
  count?: number;
}

export interface ProductStoneDetailsInput {
  diamondCarat: number | null;
  noOfDiamonds: number | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  solitaireTotalNoOfStones: number | null;
  gemstones: string | null;
  gemstonesTypeAndCount: string | null;
}

type GemstoneSource = string | Array<{ type?: string; count?: number }> | null | undefined;

export function parseProductGemstones(...sources: GemstoneSource[]): GemstoneLine[] {
  for (const raw of sources) {
    if (!raw) continue;

    if (Array.isArray(raw)) {
      const lines = raw
        .filter((item) => Boolean(item?.type))
        .map((item) => ({ type: item.type!, count: item.count }));
      if (lines.length > 0) {
        return lines;
      }
      continue;
    }

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw) as Array<{ type?: string; count?: number }>;
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
            .filter((item) => Boolean(item.type))
            .map((item) => ({ type: item.type!, count: item.count }));
        }
      } catch {
        // try next source
      }
    }
  }

  return [];
}

export function buildProductStoneDetailsInput(
  productDetail: ProductDetail,
  customization: ProductCustomization | null,
  diamondStoneType: string | null
): ProductStoneDetailsInput {
  return {
    diamondCarat: customization?.diamondCarat ?? productDetail.diamondCarat,
    noOfDiamonds: customization?.noOfDiamonds ?? productDetail.noOfDiamonds,
    diamondStoneType:
      customization?.diamondStoneType ?? diamondStoneType ?? productDetail.diamondStoneType,
    solitaireCarat: productDetail.solitaireCarat,
    solitaireClarity: productDetail.solitaireClarity,
    solitaireColor: productDetail.solitaireColor,
    solitaireTotalNoOfStones: productDetail.solitaireTotalNoOfStones,
    gemstones: customization?.gemstonesTypeAndCount ?? productDetail.gemstones,
    gemstonesTypeAndCount:
      customization?.gemstonesTypeAndCount ?? productDetail.gemstonesTypeAndCount,
  };
}

export function getGemstoneLines(input: ProductStoneDetailsInput): GemstoneLine[] {
  return parseProductGemstones(input.gemstonesTypeAndCount, input.gemstones);
}

export function hasDiamondDetails(input: ProductStoneDetailsInput): boolean {
  return Boolean(input.diamondCarat);
}

export function hasSolitaireDetails(input: ProductStoneDetailsInput): boolean {
  return Boolean(input.solitaireCarat);
}

export function hasGemstoneDetails(input: ProductStoneDetailsInput): boolean {
  return getGemstoneLines(input).length > 0;
}

export function getDiamondSummaryLabel(input: ProductStoneDetailsInput): string | null {
  if (!hasDiamondDetails(input)) return null;

  const parts = [input.noOfDiamonds, input.diamondStoneType, 'Diamonds'].filter(
    (part) => part != null && part !== ''
  );

  return parts.length > 0 ? parts.join(' ') : 'Diamonds';
}

export function getSolitaireSummaryLabel(input: ProductStoneDetailsInput): string | null {
  if (!hasSolitaireDetails(input)) return null;

  const parts = [
    input.solitaireTotalNoOfStones,
    input.solitaireClarity,
    input.solitaireColor,
    'Solitaires',
  ].filter((part) => part != null && part !== '');

  return parts.length > 0 ? parts.join(' ') : 'Solitaires';
}
